// Realtime helpers for the producer surface.
//
// Layered on top of `realtime.ts`'s generic Postgres-changes subscriber:
//   - `useProducerProjectChannel` — invalidates queries when episode rows
//     update (processing percent + state) or batch rows update.
//   - `useProducerTakesChannel`   — invalidates the review queue when new
//     pending-supervisor takes arrive.
//
// Both hooks no-op until a non-empty `orgId` is provided so they're safe to
// mount before the org context resolves.

import { useQueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'
import { producerProjectKey, producerProjectsKey } from './api/producer-projects'
import { reviewQueueKey } from './api/producer-takes'
import { subscribeCloudflareEvents } from './cloudflare-events'

export interface ProjectProgressPayload {
  project_id: string
  episode_id?: string
  /** New status if the project rolled forward. */
  status?: string
  processing_state?: string
  /** 0-100. */
  processing_percent?: number
}

/**
 * Subscribe to the org-scoped project_progress broadcast channel. The
 * server publishes to this channel from the Modal job webhooks; the SPA only
 * listens. On any payload we invalidate the matching project + episode keys.
 *
 * Returns nothing — the cleanup is wired through useEffect.
 */
export function useProducerProjectChannel(orgId: string | null | undefined): void {
  const qc = useQueryClient()
  useEffect(() => {
    if (!orgId) return
    return subscribeCloudflareEvents(orgId, 'project_progress', (event) => {
      const payload = event.payload as unknown as ProjectProgressPayload
      const projectId = payload.project_id ?? (event.aggregate_type === 'project' ? event.aggregate_id : undefined)
      const episodeId = payload.episode_id ?? (event.aggregate_type === 'episode' ? event.aggregate_id : undefined)
      void qc.invalidateQueries({ queryKey: producerProjectsKey })
      if (projectId) void qc.invalidateQueries({ queryKey: producerProjectKey(projectId) })
      if (episodeId) void qc.invalidateQueries({ queryKey: ['episode', episodeId] })
    })
  }, [orgId, qc])
}

export interface TakeBroadcastPayload {
  project_id: string
  take_id: string
}

/**
 * Subscribe to the org-scoped takes broadcast channel. Used by the take review
 * route + the project dashboard's "recent take stream" widget. We don't hand
 * payloads to consumers — they re-read from the cache after the invalidate.
 */
export function useProducerTakesChannel(
  orgId: string | null | undefined,
  projectId?: string,
): void {
  const qc = useQueryClient()
  useEffect(() => {
    if (!orgId) return
    return subscribeCloudflareEvents(orgId, 'takes', (event) => {
      const payload = event.payload as unknown as TakeBroadcastPayload
      const eventProjectId = payload.project_id
      if (!eventProjectId || (projectId && eventProjectId !== projectId)) return
      void qc.invalidateQueries({ queryKey: reviewQueueKey(eventProjectId) })
      void qc.invalidateQueries({ queryKey: producerProjectKey(eventProjectId) })
    })
  }, [orgId, projectId, qc])
}
